import { useAuthStore } from "@/stores/auth-store";

/**
 * Decode the `exp` claim from a JWT without verification (client-side only).
 * Returns the expiration timestamp in seconds, or null on any failure.
 */
export function decodeTokenExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // Base64url → Base64: replace URL-safe chars
    const payloadSegment = parts[1];
    if (!payloadSegment) return null;
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    // Restore padding required by atob() (JWT payloads often omit trailing '=')
    const paddingNeeded = (4 - (base64.length % 4)) % 4;
    const padded = base64 + "=".repeat(paddingNeeded);
    const decoded: unknown = JSON.parse(atob(padded));
    if (
      decoded &&
      typeof decoded === "object" &&
      "exp" in decoded &&
      typeof (decoded as { exp: unknown }).exp === "number"
    ) {
      return (decoded as { exp: number }).exp;
    }
    return null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    public response: Response,
    public data?: unknown
  ) {
    const serverMessage =
      data && typeof data === "object" && "error" in data
        ? (data as { error?: { message?: string } }).error?.message
        : undefined;
    super(serverMessage || `API Error: ${String(response.status)}`);
  }
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

class ApiClient {
  private baseUrl = "/api/v1";
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  /**
   * Lazily set up store subscriptions. Called on first API request or can be
   * triggered earlier. This avoids the TDZ error that occurs when the
   * ApiClient singleton is created before useAuthStore finishes initializing
   * (circular module evaluation order in the production bundle).
   */
  private ensureInitialized(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Schedule proactive refresh whenever tokens change (login, refresh, etc.)
    // Also clear timer on logout (accessToken becomes null).
    useAuthStore.subscribe((state, prevState) => {
      if (state.accessToken && state.accessToken !== prevState.accessToken) {
        this.scheduleProactiveRefresh(state.accessToken);
      } else if (!state.accessToken && prevState.accessToken) {
        // Token cleared (logout) — clean up timer
        this.clearProactiveRefresh();
      }
    });

    // Schedule for tokens already restored from persisted storage (Zustand hydration)
    const initialToken = useAuthStore.getState().accessToken;
    if (initialToken) {
      this.scheduleProactiveRefresh(initialToken);
    }

    // When tab returns from background, re-evaluate token freshness
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          const token = useAuthStore.getState().accessToken;
          if (token) {
            this.scheduleProactiveRefresh(token);
          }
        }
      });
    }
  }

  /**
   * Schedule a token refresh 1 minute before the current access token expires.
   * If the token is already near-expiry (≤0 ms remaining), triggers an immediate refresh.
   */
  private scheduleProactiveRefresh(accessToken: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    const exp = decodeTokenExp(accessToken);
    if (exp === null) return;
    const refreshAt = exp * 1000 - Date.now() - 60 * 1000; // 1 minute before expiry
    if (refreshAt <= 0) {
      void this.refreshToken();
    } else {
      this.refreshTimer = setTimeout(() => {
        void this.refreshToken();
      }, refreshAt);
    }
  }

  /**
   * Clear the proactive refresh timer (e.g., on logout).
   */
  clearProactiveRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  async request<T>(path: string, options?: RequestInit): Promise<T> {
    this.ensureInitialized();
    const token = useAuthStore.getState().accessToken;

    // Normalize caller-provided headers (which may be a plain object,
    // a Headers instance, or a [string, string][]) into a Headers object
    // so they aren't silently dropped by Object.assign.
    const headers = new Headers(options?.headers);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    // Only set Content-Type for requests with a body, but never for FormData —
    // the browser must set its own multipart/form-data boundary.
    if (options?.body && !(options.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    if (res.status === 401 && !path.startsWith("/auth/")) {
      // Attempt refresh or redirect to login (skip for auth endpoints)
      const refreshed = await this.refreshToken();
      if (!refreshed) {
        useAuthStore.getState().logout();
        window.location.href = "/login";
        throw new ApiError(res, { error: { message: "Unauthorized" } });
      }
      // Retry original request
      return this.request<T>(path, options);
    }

    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      throw new ApiError(res, data);
    }

    // Handle 204 No Content responses
    if (res.status === 204) {
      return undefined as T;
    }

    return res.json() as Promise<T>;
  }

  private async refreshToken(): Promise<boolean> {
    // If a refresh is already in-flight, reuse it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefresh();
    return this.refreshPromise;
  }

  private async _doRefresh(): Promise<boolean> {
    try {
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) return false;

      const res = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) return false;

      const data = (await res.json()) as RefreshResponse;
      useAuthStore.getState().setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      this.refreshPromise = null;
    }
  }

  get<T>(path: string) {
    return this.request<T>(path);
  }

  post<T>(path: string, body: unknown) {
    return this.request<T>(path, { method: "POST", body: JSON.stringify(body) });
  }

  put<T>(path: string, body: unknown) {
    return this.request<T>(path, { method: "PUT", body: JSON.stringify(body) });
  }

  patch<T>(path: string, body: unknown) {
    return this.request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
  }

  delete<T>(path: string) {
    return this.request<T>(path, { method: "DELETE" });
  }

  /**
   * Issue a streaming request (e.g., Server-Sent Events) that participates
   * in ApiClient's auth refresh and 401-retry logic.
   *
   * Unlike `request()`, this returns the raw `Response` object so callers
   * can read from `response.body` as a stream. The response body is NOT
   * consumed by ApiClient — callers own it.
   *
   * On 401, attempts a token refresh once and retries the request a single
   * time; if the refresh fails or the retry also returns 401, the user is
   * logged out and redirected to login (matching `request()` semantics).
   */
  async stream(path: string, options?: RequestInit): Promise<Response> {
    return this.streamWithRetry(path, options, false);
  }

  private async streamWithRetry(
    path: string,
    options: RequestInit | undefined,
    hasRetried: boolean
  ): Promise<Response> {
    this.ensureInitialized();
    const token = useAuthStore.getState().accessToken;

    // Normalize caller-provided headers (Headers / object / array) so they
    // survive being merged with the auth + Content-Type defaults below.
    const headers = new Headers(options?.headers);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (options?.body && !(options.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    if (res.status === 401 && !path.startsWith("/auth/")) {
      // Drain the failed response body to free the connection before retrying.
      await res.body?.cancel().catch(() => {});

      // If we already retried once, treat a second 401 as a hard auth failure
      // — refreshing again would just loop. Logout and redirect.
      if (hasRetried) {
        useAuthStore.getState().logout();
        window.location.href = "/login";
        throw new ApiError(res, { error: { message: "Unauthorized" } });
      }

      const refreshed = await this.refreshToken();
      if (!refreshed) {
        useAuthStore.getState().logout();
        window.location.href = "/login";
        throw new ApiError(res, { error: { message: "Unauthorized" } });
      }
      return this.streamWithRetry(path, options, true);
    }

    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      throw new ApiError(res, data);
    }

    return res;
  }
}

export const api = new ApiClient();
